<template>
  <div class="document">
    <h1>BOOKDIRECT.COM TERMS OF SERVICE</h1>
    <div>
      <p>These Terms of Service ("Terms") govern the contractual relationship between:</p>
      <p><strong>BookDirect LLC</strong> (hereinafter referred to as <strong>"BookDirect.com"</strong>), a company incorporated under the laws of the State of Delaware, USA, with its registered office at:</p>
      <p>📍 <strong>8 The Green, STE A, Dover, Delaware 19901, USA</strong></p>
      <p>and</p>

      <div>
        <p>Each <strong>user or visitor</strong> (referred to as <strong>"you"</strong> or <strong>"your"</strong>) who:</p>
        <p>(i) Accesses <strong>BookDirect.com’s</strong> website (<a href="https://www.bookdirect.com">https://www.bookdirect.com</a>, the <strong>"Website"</strong>),</p>
        <p>(ii) Uses BookDirect.com’s mobile application available on iOS and Android (the "Mobile App"),</p>
        <p>(iii) Utilizes the <strong>services</strong> offered through the <strong>Website</strong> or <strong>Mobile App</strong> (collectively, the <strong>"Services"</strong>),</p>
        <p>(iv) Interacts with <strong>BookDirect.com’s</strong> business-to-business API, or</p>
        <p>(v) Embeds <strong>BookDirect.com’s</strong> widget sub-system on their own website.</p>
        <br>
        <p>BookDirect.com and you are collectively referred to as the <strong>"Parties"</strong>, and individually as a <strong>"Party"</strong>.</p>
      </div>

    </div>

    <section>
      <h2>1. Acceptance & Modification of Terms</h2>
      <p>By accessing and using the <strong>Website, Mobile App, and Services</strong>, you expressly agree to comply with these <strong>Terms</strong> and all applicable <strong>laws and regulations</strong>.</p>
      <p>If you <strong>do not agree</strong> with any part of these <strong>Terms</strong>, you must <strong>cease using</strong> the Website, Mobile App, and Services immediately.</p>
    </section>

    <section>
      <h2>1.1 Modifications to the Terms</h2>
      <ul>
        <li>BookDirect.com reserves the right to <strong>update, amend, or modify</strong> these Terms at any time at its <strong>sole discretion</strong>.</li>
        <li>We will <strong>notify</strong> you of material changes via email or by posting an updated version on the <strong>Website and Mobile App</strong>.</li>
        <li>Your continued use of our Services after modifications constitutes <strong>acceptance</strong> of the revised Terms.</li>
      </ul>
    </section>

    <section>
      <h2>2. Description of Services</h2>
      <p>BookDirect.com provides a digital <strong>travel and accommodation booking platform </strong> that facilitates direct reservations between travellers and accommodation & activities providers. The Services include:</p>
      <ul>
        <li><strong>Hotel, Accommodation & Experiences Listings</strong> – Users can browse and book stays or activities directly from the provider.</li>
        <li><strong>Payment Processing</strong> – Secure transactions for booking payments.</li>
        <li><strong>Customer Support</strong> – Assistance regarding bookings, cancellations, and refunds.</li>
        <li><strong>API & Widget Integration</strong> – Business users can embed BookDirect.com’s booking system on their own websites.</li>
      </ul>
      <p>You acknowledge that <strong>BookDirect.com is not a travel agency or property owner</strong>; it <strong>only facilitates bookings</strong> between users and accommodation or activities providers.</p>
    </section>

    <section>
      <h2>3. User Responsibilities</h2>
      <p>You agree to:</p>
      <ul>
        <li>Provide accurate <strong> personal and payment information</strong>.</li>
        <li>Use the Services <strong>only for lawful purposes</strong>.</li>
        <li>Refrain from <strong>fraudulent, abusive, or misleading activities</strong>.</li>
      </ul>
      <p>If you are booking accommodation, you are responsible for reading the property’s <strong>cancellation policy</strong> before confirming your reservation.</p>
    </section>

    <section>
      <h2>4. Privacy & Data Protection</h2>
      <p>We comply with global <strong>privacy laws</strong>, including:</p>
      <ul>
        <li><strong>General Data Protection Regulation (GDPR)</strong> (EU)</li>
        <li><strong>California Consumer Privacy Act (CCPA)</strong> (USA)</li>
      </ul>
      <p>We collect and process <strong>personal data</strong> in accordance with our <strong>Privacy Policy</strong>, which can be accessed here.</p>
    </section>

    <section>
      <h2>5. Booking & Payment Terms</h2>
      <p>BookDirect.com offers two types of payment processing for accommodation bookings:</p>
    </section>

    <section>
      <h2>5.1 Payment Methods</h2>
      <p>Users booking accommodations through BookDirect.com may encounter the following payment methods:</p>
      <div>
        <ol>
          <li>
            <strong>Pay at Property:</strong>
            <ul>
              <li>The guest <strong>pays the total amount directly</strong> to the accommodation partner upon check-in or check-out.</li>
              <li>BookDirect.com does <strong>not collect funds in advance</strong> for these reservations.</li>
              <li>Any <strong>cancellation fees</strong> or <strong>no-show charges</strong> will be determined by the accommodation partner based on their policy.</li>
            </ul>
          </li>
          <li>
            <strong>Pay online now (Pre-payment Collected by BookDirect.com):</strong>
            <ul>
              <li>BookDirect.com <strong>collects payment from the guest at the time of booking</strong> on behalf of the accommodation partner.</li>
              <li>BookDirect.com then <strong>remits the payment</strong> to the accommodation partner <strong>after deducting any service fees or commission if applicable</strong>.</li>
              <li>The total amount charged will be clearly displayed before booking, including any applicable taxes, fees, or surcharges. Any additional fees or charges paid directly to accommodations partner may be displayed for reference only and not charged in advance.</li>
            </ul>
          </li>
        </ol>
      </div>
    </section>

    <section>
      <h2>5.1 Pricing Transparency</h2>
      <p>To ensure pricing transparency, BookDirect.com adheres to the following:</p>
      <ul>
        <li><strong>Accurate Pricing</strong>: All prices displayed include applicable taxes and fees upfront, with no hidden charges.</li>
        <li><strong>Clear Payment Terms</strong>: Users will be informed at the time of booking whether they will <strong>pay at the property</strong> or if payment will be <strong>collected in advance</strong> by BookDirect.com.</li>
        <li><strong>Currency & Exchange Rates</strong>: Prices will be shown in the user’s selected currency where applicable, but actual charges may vary by currency fluctuation.</li>
        <li><strong>Cancellation & Refund Policies</strong>: Each booking will clearly indicate whether a reservation is refundable, non-refundable, or subject to penalties, as determined by the accommodation provider.</li>
        <li><strong>Payment Security</strong>: All transactions processed by BookDirect.com comply with <strong>PCI DSS standards</strong> and <strong>GDPR regulations</strong> to ensure secure payment handling.</li>
      </ul>
    </section>

    <section>
      <h2>Chargebacks & Disputes</h2>
      <ul>
        <li>If a dispute arises regarding a <strong>prepaid booking</strong>, guests must first contact <strong>BookDirect.com’s customer support</strong> for resolution.</li>
        <li>Chargebacks initiated with a payment provider will be handled in accordance with <strong>BookDirect.com’s dispute resolution policy</strong>, which may involve providing proof of booking, cancellation policies, and refund eligibility.</li>
        <li>BookDirect.com is not responsible for <strong>disputes related to “Pay at Property” bookings</strong>, as payments are processed directly by the accommodation provider.</li>
      </ul>
    </section>

    <section>
      <h2>6. Cancellations & Refunds</h2>
      <ul>
        <li>Each hotel/accommodation has its <strong>own cancellation and refund policy</strong>.</li>
        <li>Refunds (if applicable) are subject to the property’s terms.</li>
        <li>If an issue arises, you may contact <strong>BookDirect.com’s support</strong> for mediation.</li>
      </ul>
    </section>

    <section>
      <h2>7. Disclaimer of Liability</h2>
      <p>BookDirect.com <strong> does not</strong> guarantee:</p>
      <ul>
        <li>The accuracy of accommodation listings.</li>
        <li>Availability of accommodations.</li>
        <li>Quality of services provided by third parties.</li>
      </ul>
      <p>To the fullest extent permitted by law, <strong>BookDirect.com is not liable</strong> for:</p>
      <ul>
        <li>Losses incurred from <strong>cancellations, service failures, or misrepresentations by third-party providers</strong>.</li>
        <li>Indirect or incidental damages resulting from the use of the platform.</li>
      </ul>
    </section>

    <section>
      <h2>8. Our Approach to Transparency</h2>
      <p>BookDirect.com agrees to:</p>
      <ul>
        <li>Provide <strong>clear and transparent pricing</strong> (no hidden fees).</li>
        <li>Display <strong>real-time availability</strong> of accommodations.</li>
        <li>Clearly outline <strong>cancellation and refund</strong> policies before a user completes a booking.</li>
        <li>Offer <strong>secure, GDPR-compliant data handling</strong> for all user interactions.</li>
      </ul>
      <p>By using our platform, you acknowledge that <strong>pricing and availability data</strong> is managed by accommodation providers and is subject to change.</p>
    </section>

    <section>
      <h2>9. Governing Law & Dispute Resolution</h2>
      <p>These <strong>Terms</strong> shall be governed by the laws of the <strong>State of Delaware, USA</strong>, without regard to conflict of laws principles.</p>
      <ul>
        <li><strong>International Users</strong>: If you access the Services from outside the <strong>United States</strong>, you agree to comply with local laws.</li>
        <li><strong>Dispute Resolution</strong>: Any disputes shall be resolved through <strong>binding arbitration</strong> in Delaware, USA.</li>
      </ul>
    </section>

    <section>
      <h2>10. Contact Information</h2>
      <p>For any questions regarding these Terms, please contact:</p>
      <p class="contact-info">📧 <strong>Email</strong>: admin@bookdirect.com</p>
      <p class="contact-info">📞 <strong>Phone</strong>: 1 (416) 487-7797</p>
      <p class="contact-info">📅 <strong>Effective Date</strong>: 24th January 2025</p>
    </section>
  </div>
</template>
<style scoped lang="scss" src="./documents.scss"></style>
<script setup lang="ts">
</script>
